import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import {
  HiOutlineShieldCheck,
  HiOutlineCubeTransparent,
  HiOutlineHeart,
  HiOutlineMap,
} from "react-icons/hi2";

import s from "./StaticPage.module.css";
import insurance from "../../images/insurance.jpg";
import { bankName } from "../../util/config";

const Insurance = () => {
  return (
    <Fragment>
      <Helmet>
        <title>Insurance — {bankName}</title>
        <meta
          name="description"
          content={`Select and Cover: bundle protection for travel, gadgets, breakdown, and more with ${bankName}.`}
        />
        <link rel="canonical" href="/insurance" />
      </Helmet>
      <section className={s.page}>
        <div
          className={s.hero}
          style={{ backgroundImage: `url(${insurance})` }}
        >
          <div className={s.heroOverlay} />
          <div className={s.heroContent}>
            <p className={s.eyebrow}>Select &amp; Cover</p>
            <h1>One policy, multiple protections—built around you</h1>
            <p className={s.lead}>
              Choose three or more covers—travel, tech, home emergencies,
              breakdown—and pay a single monthly premium. Adjust annually while
              keeping the simplicity of one renewal date.
            </p>
          </div>
        </div>

        <div className={s.metrics}>
          <div className={s.metric}>
            <span className={s.metricValue}>3+</span>
            <span className={s.metricLabel}>Covers to activate a bundle</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>1×</span>
            <span className={s.metricLabel}>Monthly premium &amp; renewal</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>Online</span>
            <span className={s.metricLabel}>Manage cover in digital banking</span>
          </div>
        </div>

        <div className={s.section}>
          <div className={s.sectionHeader}>
            <h2>Insurance that respects how you actually live</h2>
            <p className={s.sectionIntro}>
              Exclusive to {bankName} customers registered for online banking:
              fewer forms, clearer documents, and underwriting that focuses on
              what you need—not generic bundles you will never use.
            </p>
          </div>
          <div className={s.grid}>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineMap aria-hidden />
              </div>
              <h3>Worldwide travel confidence</h3>
              <p>
                Medical, cancellation, and baggage options with limits you can
                read upfront. Ideal for frequent flyers and families who want
                one place to check policy wording before every trip.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineCubeTransparent aria-hidden />
              </div>
              <h3>Tech &amp; everyday essentials</h3>
              <p>
                Mobile and gadget cover for the devices you rely on—aligned with
                realistic excess levels and repair-or-replace clarity.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineShieldCheck aria-hidden />
              </div>
              <h3>Motor breakdown &amp; home resilience</h3>
              <p>
                Roadside assistance tiers and home emergency options so
                unexpected failures do not derail your week—or your budget.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineHeart aria-hidden />
              </div>
              <h3>Household-wide thinking</h3>
              <p>
                Cover can extend to your partner and children at home—or
                students in full-time education when they return between terms.
                We spell out who is included in plain language.
              </p>
            </div>
          </div>
        </div>

        <div className={s.split}>
          <div className={s.splitText}>
            <h2>Annual flexibility, minimum commitment</h2>
            <p>
              Each year you can refine your mix—as long as three or more cover
              types remain active—so your policy tracks life changes (new
              devices, different travel patterns, a new car).
            </p>
            <p>
              Cancelling mid-term may incur short-rate charges; cooling-off
              rights apply from inception. Claims are assessed against the
              policy booklet—keep copies of high-value purchases where required.
            </p>
          </div>
          <div className={s.splitText}>
            <div className={s.highlight}>
              <p>
                <strong>Institutional strength.</strong> {bankName} Insurance
                Services Company Limited arranges cover with rated insurers;
                you receive insurer documentation alongside our service
                standards and complaints process.
              </p>
            </div>
          </div>
        </div>

        <p className={s.trustStrip}>
          {bankName} Insurance Services is authorised and regulated by the
          Swiss Financial Market Supervisory Authority (FINMA). Select and
          Cover is subject to eligibility, underwriting, and policy terms.
          Always read the Insurance Product Information Document and policy
          wording.
        </p>
      </section>
    </Fragment>
  );
};

export default Insurance;
